import { createContext, useContext, useReducer, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { captureException } from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { Organization, OrganizationMember } from '@/types';
import { User, Session, type AuthChangeEvent } from '@supabase/supabase-js';
import { isSsoSession } from '@/shared/ssoSession';
import { isPendingSsoCallbackSession, isStepUpReassertionReturn, settleSsoCallbackAdmission } from '@/lib/ssoCallbackAdmission';
import {
  type AppBootstrapReadModelResponse,
  type BootstrapSubscriptionReadModelResponse,
  type BootstrapSupplierLimitReadModelResponse,
  invokeAppBootstrapReadModel,
  invokeOrganizationContextReadModel,
} from '@/lib/readModels';
import { captureInvoluntarySignOut } from '@/lib/authRecoveryTelemetry';
import {
  isAuthInvalidFunctionInvokeError,
  isFunctionInvokeAbortError,
} from '@/lib/functionInvokeErrors';
import {
  endSessionSignedOutByAuthLibrary,
  resetSessionExpiryGuard,
  setSessionExpiryHandler,
} from '@/lib/protectedFunctionAuth';
import { dismissAllToasts } from '@/hooks/use-toast';
import { isClientSessionStored, sessionIdOfAccessToken, signOutThroughAuthLibrary } from '@/lib/authLibrarySignOut';
import { isSessionEnded, publishSessionEndedAcrossTabs } from '@/lib/sessionEnded';
import { syncMarketingAuthHintCookie } from '@/lib/marketingAuthHint';
import { clearLocalAuthState, clearPendingSignupVerification, isUserEmailVerified } from '@/lib/auth';
import {
  carryForwardCachedMspCustomerOrgs,
  clearOrganizationContextCache,
  readOrganizationContextCache,
  writeOrganizationContextCache,
  type OrganizationContextCacheValue,
} from '@/lib/organizationContextCache';
import { getCachedDepartments, hydrateDepartmentsCache, type Department } from '@/hooks/useDepartments';
import { releaseLoadingAfterTimeout } from './organizationLoading';

type OrgBootstrapError = 'timeout' | 'load_failed' | null;

function reportSessionReconciliationFailure(error: unknown): void {
  console.error('Failed to reconcile signed-out session:', error);
  try {
    captureException(error, {
      fingerprint: ['auth.session_reconciliation'],
      tags: { function_name: 'auth.getSession', auth_recovery: 'reconciliation_failed' },
    });
  } catch (captureError) {
    // Telemetry must never prevent the fail-closed auth transition.
    console.error('Failed to capture session reconciliation failure:', captureError);
  }
}

export type OrganizationSwitchSource =
  | 'legacy'
  | 'organization-switcher'
  | 'command-palette-customer'
  | 'customers-dashboard'
  | 'customer-profile-exit'
  | 'supplier-plus'
  | 'advisory-email';

export interface OrganizationSwitchRequest {
  targetOrgId: string;
  source: OrganizationSwitchSource;
  targetKind?: 'direct' | 'managed-customer';
  mspHomeOrgId?: string;
}

interface OrganizationContextType {
  user: User | null;
  session: Session | null;
  organization: Organization | null;
  membership: OrganizationMember | null;
  organizations: Organization[];
  mspCustomerOrgs: Organization[];
  departments: Department[];
  departmentsHydrated: boolean;
  isMspOrg: boolean;
  isLoading: boolean;
  bootstrapError: OrgBootstrapError;
  isMspContext: boolean;
  mspHomeOrg: Organization | null;
  /**
   * ENT-1947/codex: the MSP home org **id** from the bootstrap snapshot. Unlike
   * `mspHomeOrg` (derived from the background-hydrated `organizations` list, so
   * transiently null on a hard refresh while switched into a managed customer)
   * this is set immediately, so guards that must not flicker open during hydration
   * (e.g. "is the active org the MSP home org?") should use this id.
   */
  mspHomeOrgId: string | null;
  bootstrapSubscription: BootstrapSubscriptionReadModelResponse | null;
  bootstrapSupplierLimit: BootstrapSupplierLimitReadModelResponse | null;
  switchOrganizationContext: (request: OrganizationSwitchRequest) => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
  returnToMspOrg: () => Promise<void>;
  // ENT-4200 (Fix 5b): an optional user override lets a caller that already holds
  // the authenticated user (e.g. a fresh SSO callback whose SIGNED_IN the context
  // has not adopted yet) force a fetch that would otherwise no-op on a null
  // `state.user`. Omit the override for the ordinary "refetch the current user".
  refetch: (override?: OrganizationRefetchOverride) => Promise<void>;
  /**
   * ENT-4713 (Codex R1 P1): publish a just-admitted initial SAML callback session
   * to the authenticated provider tree WITHOUT an organisation bootstrap. The
   * initial SAML session is deliberately withheld on raw `SIGNED_IN` until
   * `sso-claim-membership` succeeds; a claim that resolves to a route without org
   * context (`/access-pending`) or a lost-response fallback must still expose the
   * admitted user here, or `EnterpriseSsoRouteGuard` sees `user=null` and bounces
   * the recipient to `/auth`. Idempotent; safe to call before `refetch`.
   */
  publishAdmittedSession: (session: Session) => void;
}

export interface OrganizationRefetchOverride {
  userId?: string;
  sessionUserId?: string;
  accessToken?: string;
  /**
   * A just-admitted callback session. Initial SAML callbacks are deliberately
   * withheld from global authenticated providers until sso-claim-membership has
   * persisted their durable admission; the callback passes this exact session
   * here to expose it and bootstrap in that safe order.
   */
  session?: Session;
  /**
   * ENT-4200 (Fix 5b, Codex R5 P1): put the context back into `isLoading=true`
   * synchronously (before the fetch's first await) and release it only when this
   * fetch — or a newer one — settles. For callers that know the membership state
   * changed underneath an already-completed bootstrap (the post-SSO-claim refetch)
   * and route to a page whose loading≠empty gate must show skeletons rather than
   * an empty shell. Off by default: in-app refetches (Team settings, Onboarding,
   * AppLayout's retry loop — which keys on `isLoading`) must not flash loading.
   */
  markLoading?: boolean;
}

/**
 * A first SAML callback is provisional until sso-claim-membership writes the
 * durable admission consumed by PostgREST and Edge guards. Do not expose its
 * user to root providers (which immediately read profile, toast and org data).
 *
 * A step-up reassertion is different: it is a return leg proven by the one-shot
 * marker set before the IdP redirect, so it is not treated as an initial callback
 * here and is not deferred. Requiring that marker means a crafted `stepup_resume`
 * query cannot bypass the first-login admission ordering.
 *
 * NOTE: the *pre-redirect* session was admitted, but `signInWithSSO` mints a NEW
 * `sso/saml` session for the reassertion that no claim admits — that new session's
 * admission is a separate ENT-4508 gap tracked in ENT-4725. This predicate governs
 * only initial-callback publication ordering; the DB gate fails closed regardless.
 */
export function shouldDeferInitialSsoAdmission(session: Session | null): boolean {
  if (typeof window === 'undefined' || !isSsoSession(session)) {
    return false;
  }

  // ENT-4771: the `?sso_return=1` marker is appended only by the app's own
  // `/auth` controller. An IdP-initiated login (Okta tile → GoTrue Site URL) or
  // any other `redirect_to` lands WITHOUT it, so also key on the callback
  // itself: the SAML session GoTrue delivered in this page's boot URL fragment,
  // until its claim-admitted publication settles it.
  const search = new URLSearchParams(window.location.search);
  const isInitialCallback = search.get('sso_return') === '1' || isPendingSsoCallbackSession(session);
  if (!isInitialCallback) {
    return false;
  }
  if (isStepUpReassertionReturn(window.location.search)) {
    // Step-up return (marker AND a valid stepup_resume — a stale marker alone
    // is not one; Codex P2, PR #3256): published without a claim, so never
    // leave a pending callback record behind for it (Codex P1, PR #3256).
    if (isPendingSsoCallbackSession(session)) {
      settleSsoCallbackAdmission();
    }
    return false;
  }
  return true;
}

// ── Reducer for batched state updates ──────────────────────────
export interface OrgState {
  user: User | null;
  session: Session | null;
  organization: Organization | null;
  membership: OrganizationMember | null;
  organizations: Organization[];
  mspCustomerOrgs: Organization[];
  departments: Department[];
  departmentsHydrated: boolean;
  isMspOrg: boolean;
  isLoading: boolean;
  bootstrapError: OrgBootstrapError;
  isMspContext: boolean;
  mspHomeOrgId: string | null;
  bootstrapSubscription: BootstrapSubscriptionReadModelResponse | null;
  bootstrapSupplierLimit: BootstrapSupplierLimitReadModelResponse | null;
}

export type OrgAction =
  | { type: 'SET_AUTH'; user: User | null; session: Session | null; preserveUserReference?: boolean }
  | { type: 'SET_BOOTSTRAP_DATA'; organization: Organization | null; membership: OrganizationMember | null; isMspOrg: boolean; isMspContext: boolean; mspHomeOrgId: string | null; subscription: BootstrapSubscriptionReadModelResponse | null; supplierLimit: BootstrapSupplierLimitReadModelResponse | null }
  | { type: 'SET_ORG_DATA'; organization: Organization | null; membership: OrganizationMember | null; organizations: Organization[]; mspCustomerOrgs: Organization[]; departments: Department[]; isMspOrg: boolean; isMspContext: boolean; mspHomeOrgId: string | null }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_BOOTSTRAP_ERROR'; error: OrgBootstrapError }
  | { type: 'CLEAR_ORG_DATA' }
  | { type: 'SWITCH_ORG'; organization: Organization; membership: OrganizationMember; departments: Department[]; isMspOrg: boolean; isMspContext: boolean; mspHomeOrgId: string | null }
  | { type: 'ADD_CUSTOMER_ORG'; org: Organization };

function shouldPreserveAuthUserReference(currentUser: User | null, nextUser: User | null): boolean {
  // ENT-3079: preservation is identity-guarded — same user AND unchanged user
  // data (`updated_at` bumps whenever profile/metadata changes). This lets the
  // listener request preservation for benign re-emits (TOKEN_REFRESHED, and the
  // SIGNED_IN supabase-js fires on tab-visibility recovery) without ever hiding
  // a genuine user-data update behind a stale reference.
  return Boolean(
    currentUser &&
      nextUser &&
      currentUser.id === nextUser.id &&
      currentUser.updated_at === nextUser.updated_at,
  );
}

export function orgReducer(state: OrgState, action: OrgAction): OrgState {
  switch (action.type) {
    case 'SET_AUTH':
      return {
        ...state,
        user: action.preserveUserReference && shouldPreserveAuthUserReference(state.user, action.user)
          ? state.user
          : action.user,
        session: action.session,
      };
    case 'SET_ORG_DATA':
      return {
        ...state,
        organization: action.organization,
        membership: action.membership,
        organizations: action.organizations,
        mspCustomerOrgs: action.mspCustomerOrgs,
        departments: action.departments,
        departmentsHydrated: true,
        isMspOrg: action.isMspOrg,
        isMspContext: action.isMspContext,
        mspHomeOrgId: action.mspHomeOrgId,
        bootstrapError: null,
      };
    case 'SET_BOOTSTRAP_DATA': {
      // ENT-2659: the bootstrap snapshot updates the active org but does NOT carry
      // the (now active-MSP-scoped) mspCustomerOrgs — that arrives via the deferred
      // SET_ORG_DATA read. On a real context switch the active org changes here first,
      // so drop the previous org's mspCustomerOrgs; otherwise the /team and MSP Insights
      // selectors — now that the client-side scope filter is removed — could briefly
      // render the prior MSP's customers under the new org until the deferred read lands.
      // A same-org re-bootstrap (e.g. the cache first-paint path, which dispatches
      // SET_ORG_DATA with the cached list immediately before SET_BOOTSTRAP_DATA for the
      // same org) preserves the already-hydrated list.
      const orgChanged = action.organization?.id !== state.organization?.id;
      return {
        ...state,
        organization: action.organization,
        membership: action.membership,
        isMspOrg: action.isMspOrg,
        isMspContext: action.isMspContext,
        mspHomeOrgId: action.mspHomeOrgId,
        mspCustomerOrgs: orgChanged ? [] : state.mspCustomerOrgs,
        bootstrapSubscription: action.subscription,
        bootstrapSupplierLimit: action.supplierLimit,
        bootstrapError: null,
      };
    }
    case 'SET_LOADING':
      // Bail out on a no-op so the generation-guarded release every settling
      // fetch performs (ENT-4200 R5) never re-renders consumers needlessly.
      return state.isLoading === action.isLoading ? state : { ...state, isLoading: action.isLoading };
    case 'SET_BOOTSTRAP_ERROR':
      return { ...state, bootstrapError: action.error };
    case 'CLEAR_ORG_DATA':
      return {
        ...state,
        organization: null,
        membership: null,
        organizations: [],
        mspCustomerOrgs: [],
        departments: [],
        departmentsHydrated: false,
        isMspOrg: false,
        isMspContext: false,
        mspHomeOrgId: null,
        bootstrapSubscription: null,
        bootstrapSupplierLimit: null,
        bootstrapError: null,
      };
    case 'SWITCH_ORG':
      return {
        ...state,
        organization: action.organization,
        membership: action.membership,
        departments: action.departments,
        departmentsHydrated: true,
        isMspOrg: action.isMspOrg,
        isMspContext: action.isMspContext,
        mspHomeOrgId: action.mspHomeOrgId,
        bootstrapError: null,
      };
    case 'ADD_CUSTOMER_ORG':
      return {
        ...state,
        mspCustomerOrgs: [...state.mspCustomerOrgs, action.org],
      };
    default:
      return state;
  }
}

const initialState: OrgState = {
  user: null,
  session: null,
  organization: null,
  membership: null,
  organizations: [],
  mspCustomerOrgs: [],
  departments: [],
  departmentsHydrated: false,
  isMspOrg: false,
  isLoading: true,
  bootstrapError: null,
  isMspContext: false,
  mspHomeOrgId: null,
  bootstrapSubscription: null,
  bootstrapSupplierLimit: null,
};

const FETCH_COOLDOWN_MS = 5 * 60 * 1000;
const BOOTSTRAP_COOLDOWN_MS = 1_000;
const BOOTSTRAP_LOADING_RELEASE_TIMEOUT_MS = 25_000;
const ORGANIZATION_SELECTION_STORAGE_KEYS = ['currentOrgId', 'currentOrgUserId', 'isMspContext', 'mspHomeOrgId'] as const;
const E2E_AUTH_REFRESH_HOOK_STORAGE_KEY = '__supplyGuardE2eAuthRefreshEnabled';

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Auth events that should NOT trigger a re-fetch
const SKIP_REFETCH_EVENTS = new Set(['TOKEN_REFRESHED']);

interface E2eAuthRefreshResult {
  ok: boolean;
  event?: 'TOKEN_REFRESHED';
  userId: string | null;
  error?: string;
}

declare global {
  interface Window {
    __supplyGuardE2eRefreshAuthSession?: () => Promise<E2eAuthRefreshResult>;
  }
}

function shouldExposeE2eAuthRefreshHook() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  if (hostname === 'app.supply-guard.io') {
    return false;
  }

  const isNonProductionHost = (
    hostname === '127.0.0.1'
    || hostname === 'localhost'
    || hostname === 'enterprise-staging.vercel.app'
    || hostname === 'staging.supply-guard.io'
    || hostname.endsWith('.vercel.app')
  );

  return isNonProductionHost && window.localStorage.getItem(E2E_AUTH_REFRESH_HOOK_STORAGE_KEY) === 'true';
}

function createTokenRefreshEventWaiter(timeoutMs = 10_000) {
  let timeoutId: number | null = null;
  let unsubscribe: (() => void) | null = null;

  const cleanup = () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    unsubscribe?.();
    unsubscribe = null;
  };

  const promise = new Promise<Pick<E2eAuthRefreshResult, 'event' | 'userId'>>((resolve, reject) => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== 'TOKEN_REFRESHED') {
        return;
      }

      cleanup();
      resolve({
        event: 'TOKEN_REFRESHED',
        userId: session?.user?.id ?? null,
      });
    });
    unsubscribe = () => subscription.unsubscribe();

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for TOKEN_REFRESHED'));
    }, timeoutMs);
  });

  return { promise, cleanup };
}

function clearPersistedOrganizationSelection(storage: Pick<Storage, 'removeItem'>) {
  for (const key of ORGANIZATION_SELECTION_STORAGE_KEYS) {
    storage.removeItem(key);
  }
}

function syncPersistedOrganizationSelection(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  cached: OrganizationContextCacheValue | null,
  userId: string,
) {
  const explicitCurrentOrgId = storage.getItem('currentOrgId');

  if (!cached?.organization) {
    if (explicitCurrentOrgId && (
      storage.getItem('currentOrgUserId') === userId
      || shouldPreserveUnauthenticatedOrganizationSelection(storage)
    )) {
      return;
    }
    clearPersistedOrganizationSelection(storage);
    return;
  }

  if (explicitCurrentOrgId) {
    return;
  }

  storage.setItem('currentOrgId', cached.organization.id);
  storage.setItem('currentOrgUserId', userId);
  storage.setItem('isMspContext', String(cached.isMspContext));

  if (cached.mspHomeOrgId) {
    storage.setItem('mspHomeOrgId', cached.mspHomeOrgId);
    return;
  }

  storage.removeItem('mspHomeOrgId');
}

function shouldPreserveUnauthenticatedOrganizationSelection(
  storage: Pick<Storage, 'getItem'>,
) {
  if (typeof window === 'undefined') {
    return false;
  }

  const hasExplicitOrgSelection = Boolean(storage.getItem('currentOrgId'));
  if (!hasExplicitOrgSelection) {
    return false;
  }

  const currentUrl = new URL(window.location.href);
  return currentUrl.pathname === '/auth' && currentUrl.searchParams.has('returnTo');
}

function canHydrateFromCachedOrganizationSelection(
  storage: Pick<Storage, 'getItem'>,
  cached: OrganizationContextCacheValue | null,
) {
  if (!cached?.organization) {
    return false;
  }

  const explicitCurrentOrgId = storage.getItem('currentOrgId');
  if (explicitCurrentOrgId && explicitCurrentOrgId !== cached.organization.id) {
    return false;
  }

  const explicitIsMspContext = storage.getItem('isMspContext');
  if (explicitIsMspContext && explicitIsMspContext !== String(cached.isMspContext)) {
    return false;
  }

  const explicitMspHomeOrgId = storage.getItem('mspHomeOrgId');
  if ((explicitMspHomeOrgId ?? null) !== (cached.mspHomeOrgId ?? null)) {
    return explicitMspHomeOrgId === null && cached.mspHomeOrgId === null;
  }

  return true;
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(orgReducer, initialState);
  // ENT-6102: keep the latest authenticated session identity through SIGNED_OUT.
  // Teardown itself emits SIGNED_OUT; only a replacement sign-in supersedes its
  // authority. session_id survives token refreshes of the same dying session.
  const lastAuthenticatedSessionIdentityRef = useRef<string | null>(null);

  // ENT-2371 / ENT-5492: the ONLY owner of involuntary sign-out. It runs once
  // per dead session, when `invokeProtectedFunction` could not recover a 401 by
  // refreshing the stored session (D1). The auth library signs the session out
  // itself — so every tab learns of it — and only then are the app's own keys
  // cleared (D2). SIGNED_OUT drops the user; the route guards carry the current
  // route to `/auth?returnTo=…`, where the `sessionEnded` latch shows the one
  // "Your session ended" notice and silences the burst of failing callers (D3).
  // One readable Sentry warning per sign-out (D4).
  useEffect(() => {
    let active = true;
    setSessionExpiryHandler(({ functionName, recovery }) => {
      const endingSessionIdentity = lastAuthenticatedSessionIdentityRef.current;
      const endingSessionId = heldSessionIdRef.current;
      console.warn('[org] terminal auth failure; signing out', { functionName });
      try {
        captureInvoluntarySignOut(recovery, window.location.pathname);
      } catch (captureError) {
        console.error('Failed to capture involuntary sign-out to Sentry:', captureError);
      }
      dismissAllToasts();
      // Publish WHY before the library broadcasts the sign-out, so every other
      // tab shows the same notice and stays quiet (Codex round 2).
      publishSessionEndedAcrossTabs();
      void (async () => {
        const outcome = await signOutThroughAuthLibrary('local', { expectedSessionId: endingSessionId });
        if (outcome === 'superseded' || outcome === 'aborted' || !active || lastAuthenticatedSessionIdentityRef.current !== endingSessionIdentity) {
          return;
        }
        clearLocalAuthState();
        dispatch({ type: 'SET_AUTH', user: null, session: null });
        dispatch({ type: 'CLEAR_ORG_DATA' });
      })();
    });

    return () => {
      active = false;
      setSessionExpiryHandler(null);
    };
  }, [dispatch]);

  const lastFetchRef = useRef<number>(0);
  const inFlightFetchRef = useRef<Promise<void> | null>(null);
  // ENT-4200 (Fix 5b, Codex R4): monotonic fetch generation. Since a forced
  // post-SSO-claim fetch no longer coalesces onto an in-flight pre-claim fetch
  // (R2 P2), the two run concurrently; the LATEST-started fetch is authoritative
  // and an older one must not apply its (possibly stale `no_memberships`) result
  // after it — this ref is the supersession token that guards that.
  const fetchGenerationRef = useRef<number>(0);
  const lastAuthenticatedUserIdRef = useRef<string | null>(null);
  // ENT-5492: the session this tab holds, so a SIGNED_OUT can tell an app
  // sign-out of THIS session from the library ending it as dead.
  const heldSessionIdRef = useRef<string | null>(null);
  const bootstrapUserIdRef = useRef<string | null>(null);
  const lastBootstrapAtRef = useRef<{ userId: string; startedAt: number } | null>(null);
  const bootstrapSubscriptionRef = useRef<BootstrapSubscriptionReadModelResponse | null>(null);

  // Track org ID in a ref so fetchOrganizations doesn't depend on state.organization
  const hasOrgRef = useRef(false);
  hasOrgRef.current = !!state.organization;

  const applyBootstrapSnapshot = useCallback((
    userId: string,
    response: AppBootstrapReadModelResponse,
  ) => {
    if (!response.organization || !response.membership) {
      dispatch({ type: 'CLEAR_ORG_DATA' });
      return;
    }

    localStorage.setItem('currentOrgId', response.organization.id);
    localStorage.setItem('currentOrgUserId', userId);
    localStorage.setItem('isMspContext', String(response.isMspContext));
    if (response.mspHomeOrgId) {
      localStorage.setItem('mspHomeOrgId', response.mspHomeOrgId);
    } else {
      localStorage.removeItem('mspHomeOrgId');
    }

    dispatch({
      type: 'SET_BOOTSTRAP_DATA',
      organization: response.organization,
      membership: response.membership,
      isMspOrg: response.isMspOrg,
      isMspContext: response.isMspContext,
      mspHomeOrgId: response.mspHomeOrgId,
      subscription: response.subscription,
      supplierLimit: response.supplierLimit,
    });

    const cached = readOrganizationContextCache(localStorage, userId);
    writeOrganizationContextCache(localStorage, {
      userId,
      organization: response.organization,
      membership: response.membership,
      organizations: cached?.organizations ?? [],
      // ENT-2659: never carry a prior org's scoped customers into the new org's cache.
      mspCustomerOrgs: carryForwardCachedMspCustomerOrgs(cached, response.organization.id),
      departments: cached?.departments ?? getCachedDepartments(response.organization.id),
      isMspOrg: response.isMspOrg,
      isMspContext: response.isMspContext,
      mspHomeOrgId: response.mspHomeOrgId,
      subscription: response.subscription,
      supplierLimit: response.supplierLimit,
    });
  }, []);

  const applyOrganizationSnapshot = useCallback((
    userId: string,
    response: Awaited<ReturnType<typeof invokeOrganizationContextReadModel>>,
  ) => {
    if (!response.organization || !response.membership) {
      dispatch({ type: 'CLEAR_ORG_DATA' });
      return;
    }

    localStorage.setItem('currentOrgId', response.organization.id);
    localStorage.setItem('currentOrgUserId', userId);
    localStorage.setItem('isMspContext', String(response.isMspContext));
    if (response.mspHomeOrgId) {
      localStorage.setItem('mspHomeOrgId', response.mspHomeOrgId);
    } else {
      localStorage.removeItem('mspHomeOrgId');
    }

    const existingCache = readOrganizationContextCache(localStorage, userId);
    writeOrganizationContextCache(localStorage, {
      userId,
      organization: response.organization,
      membership: response.membership,
      organizations: response.organizations,
      mspCustomerOrgs: response.mspCustomerOrgs,
      departments: response.departments as Department[],
      isMspOrg: response.isMspOrg,
      isMspContext: response.isMspContext,
      mspHomeOrgId: response.mspHomeOrgId,
      subscription: existingCache?.subscription ?? bootstrapSubscriptionRef.current,
      supplierLimit: existingCache?.supplierLimit ?? null,
    });

    hydrateDepartmentsCache(response.organization.id, response.departments as Department[]);

    dispatch({
      type: 'SET_ORG_DATA',
      organization: response.organization,
      membership: response.membership,
      organizations: response.organizations,
      mspCustomerOrgs: response.mspCustomerOrgs,
      departments: response.departments as Department[],
      isMspOrg: response.isMspOrg,
      isMspContext: response.isMspContext,
      mspHomeOrgId: response.mspHomeOrgId,
    });
  }, []);

  useEffect(() => {
    bootstrapSubscriptionRef.current = state.bootstrapSubscription;
  }, [state.bootstrapSubscription]);

  // ENT-4200 (Fix 5b, Codex R5): bound how long `isLoading` may stay raised for a
  // fetch generation. On the 25s release only the LATEST generation may surface the
  // `timeout` error / drop loading — a superseded generation's timer must not clobber
  // the state a newer fetch owns. Used by the mount bootstrap and by `markLoading`
  // refetches so a fired (non-awaited) post-SSO-claim refetch can never leave a
  // destination page on a permanent skeleton.
  const armLoadingReleaseTimeout = useCallback((fetchPromise: Promise<void>, generation: number) => (
    releaseLoadingAfterTimeout(fetchPromise, BOOTSTRAP_LOADING_RELEASE_TIMEOUT_MS)
      .then(({ timedOut }) => {
        if (timedOut && generation === fetchGenerationRef.current) {
          dispatch({ type: 'SET_BOOTSTRAP_ERROR', error: 'timeout' });
          dispatch({ type: 'SET_LOADING', isLoading: false });
        }
      })
  ), []);

  const fetchOrganizations = useCallback((
    userId: string,
    options: {
      force?: boolean;
      accessToken?: string | null;
      sessionUserId?: string | null;
      markLoading?: boolean;
    } = {},
  ) => {
    const { force = false, accessToken, sessionUserId, markLoading = false } = options;
    if (!force && inFlightFetchRef.current) {
      console.debug('[org] fetchOrganizations:reusing in-flight request');
      return inFlightFetchRef.current;
    }

    const now = Date.now();
    if (!force && hasOrgRef.current && (now - lastFetchRef.current) < FETCH_COOLDOWN_MS) {
      console.debug('[org] fetchOrganizations:skipped (fresh data)');
      return Promise.resolve();
    }
    lastFetchRef.current = now;
    // ENT-4200 (Fix 5b, Codex R4/R5): claim the newest generation synchronously,
    // before any await — concurrent fetches therefore receive strictly increasing
    // tokens in start order. INVARIANT (R5): only the latest-started generation may
    // mutate shared org / auth / loading / error state; every mutation path below
    // (success apply, no_memberships clear, deferred hydration, all catch branches,
    // the loading release, the loading-release timeout) is gated on
    // `isLatestFetchGeneration()`.
    const myFetchGeneration = ++fetchGenerationRef.current;
    const isLatestFetchGeneration = () => myFetchGeneration === fetchGenerationRef.current;
    if (markLoading) {
      // R5 P1: raised synchronously so a caller can fire this fetch, navigate at
      // once, and have the destination's loading≠empty gate see `isLoading` before
      // its first paint.
      dispatch({ type: 'SET_LOADING', isLoading: true });
    }

    const task = (async () => {
      const fetchId = `${userId}:${now}`;
      console.debug('[org] fetchOrganizations:start', { fetchId });
      const savedOrgId = localStorage.getItem('currentOrgId');
      const savedIsMspContext = localStorage.getItem('isMspContext') === 'true';
      const savedMspHomeOrgId = localStorage.getItem('mspHomeOrgId');
      let selectedOrgId: string | null = null;

      try {
        const bootstrapResponse = await invokeAppBootstrapReadModel({
          currentOrgId: savedOrgId,
          isMspContext: savedIsMspContext,
          mspHomeOrgId: savedMspHomeOrgId,
        }, {
          expectedUserId: userId,
          accessToken: accessToken ?? undefined,
          sessionUserId: sessionUserId ?? undefined,
          // ENT-4200 (Fix 5b, Codex R2 P2): a forced fetch (e.g. the post-SSO-claim
          // refetch) must observe a fresh snapshot, so give it its own in-flight
          // generation rather than coalescing onto a pre-claim request that may have
          // read the DB before the membership existed.
          forceFreshGeneration: force ? fetchId : undefined,
        });

        // ENT-4200 (Fix 5b, Codex R4): a newer fetch started while this one was in
        // flight — it is authoritative, so discard this (now stale) result before it
        // can apply. Without this, a slower pre-claim `no_memberships` response would
        // reach CLEAR_ORG_DATA below and wipe the membership a newer forced fetch
        // already applied, re-creating the empty post-SSO shell.
        if (!isLatestFetchGeneration()) {
          console.debug('[org] fetchOrganizations:superseded', { fetchId });
          return;
        }

        if (!bootstrapResponse.membership || !bootstrapResponse.organization) {
          console.debug('[org] fetchOrganizations:no_memberships', { fetchId });
          dispatch({ type: 'CLEAR_ORG_DATA' });
          return;
        }

        selectedOrgId = bootstrapResponse.organization.id;
        applyBootstrapSnapshot(userId, bootstrapResponse);
        void invokeOrganizationContextReadModel({
          currentOrgId: bootstrapResponse.organization.id,
          isMspContext: bootstrapResponse.isMspContext,
          mspHomeOrgId: bootstrapResponse.mspHomeOrgId,
        }, {
          expectedUserId: userId,
          accessToken: accessToken ?? undefined,
          sessionUserId: sessionUserId ?? undefined,
        }).then((response) => {
          // R5 audit: the deferred hydration of a superseded generation must not
          // land on top of the snapshot a newer fetch applied.
          if (!isLatestFetchGeneration()) {
            console.debug('[org] fetchOrganizations:superseded hydration', { fetchId });
            return;
          }
          if (localStorage.getItem('currentOrgId') !== bootstrapResponse.organization.id) {
            console.debug('[org] fetchOrganizations:skipped stale hydration', {
              fetchId,
              selectedOrgId: bootstrapResponse.organization.id,
              currentOrgId: localStorage.getItem('currentOrgId'),
            });
            return;
          }

          applyOrganizationSnapshot(userId, response);
        }).catch((error) => {
          if (!isFunctionInvokeAbortError(error)) {
            console.error('Error hydrating organisation context details:', error);
          }
        });
      } catch (error) {
        if (isFunctionInvokeAbortError(error)) {
          console.debug('[org] fetchOrganizations:aborted', { fetchId });
          return;
        }

        // ENT-4200 (Fix 5b, Codex R5 P2): the failure path is guarded exactly like
        // the success path. A superseded (older) request that rejects after a newer
        // fetch already populated the org must not CLEAR_ORG_DATA / raise
        // `load_failed` — nor, for an auth-classified error, sign the session out;
        // the newer request on the same session is the authoritative verdict.
        if (!isLatestFetchGeneration()) {
          console.debug('[org] fetchOrganizations:superseded failure ignored', { fetchId });
          return;
        }

        // ENT-5492 (D1): a 401 here already went through the stored-session
        // retry/refresh, and this caller never clears auth state itself. If the
        // session ended, the unified session-expiry handler owns the sign-out and
        // the one /auth notice: stay silent. Otherwise the session is alive (a
        // transient refresh failure, or a request made for another identity):
        // fall through to the retryable load error, never an empty shell.
        if (isAuthInvalidFunctionInvokeError(error)) {
          if (isSessionEnded()) {
            console.warn('[org] fetchOrganizations:session ended; session-expiry handler owns recovery', { fetchId });
            return;
          }
          console.warn('[org] fetchOrganizations:auth rejected on a live session; surfacing load error', { fetchId });
        }

        console.error('Error fetching organisation context:', error);
        dispatch({ type: 'CLEAR_ORG_DATA' });
        dispatch({ type: 'SET_BOOTSTRAP_ERROR', error: 'load_failed' });
        return;
      } finally {
        // R5 P1: the latest generation settling (however it settled) means the
        // context is resolved — release loading. A superseded generation never
        // touches it; the newer fetch that superseded it owns the release.
        if (isLatestFetchGeneration()) {
          dispatch({ type: 'SET_LOADING', isLoading: false });
        }
      }

      console.debug('[org] fetchOrganizations:done', {
        fetchId,
        selectedOrgId,
      });
    })();

    const wrappedTask = task.finally(() => {
      if (inFlightFetchRef.current === wrappedTask) {
        inFlightFetchRef.current = null;
      }
    });

    inFlightFetchRef.current = wrappedTask;
    if (markLoading) {
      void armLoadingReleaseTimeout(wrappedTask, myFetchGeneration);
    }
    return wrappedTask;
  }, [applyBootstrapSnapshot, applyOrganizationSnapshot, armLoadingReleaseTimeout]);

  // ENT-4713 (Codex R1 P1): expose the deferred initial SAML session to the
  // authenticated provider tree independently of the organisation bootstrap. The
  // first SAML callback did not publish auth state on raw SIGNED_IN; its claim
  // succeeded immediately before this call, so publish the exact now-admitted
  // session. Do not read it back or fabricate it. Idempotent.
  const publishAdmittedSession = useCallback((session: Session) => {
    // ENT-4771: the boot callback session is now admitted — stop deferring it
    // (and release reads held on it, e.g. the root toast appearance).
    settleSsoCallbackAdmission();
    dispatch({
      type: 'SET_AUTH',
      user: session.user,
      session,
      preserveUserReference: true,
    });
    syncMarketingAuthHintCookie(true);
    resetSessionExpiryGuard();
    lastAuthenticatedUserIdRef.current = session.user.id;
  }, []);

  const refetch = useCallback(async (override?: OrganizationRefetchOverride) => {
    // ENT-4200 (Fix 5b): prefer the caller-supplied authenticated user so a fresh
    // SSO callback can force a fetch before the context's own SIGNED_IN reducer
    // commits `state.user`. Without the override this would silently no-op on the
    // still-null `state.user` and navigation would proceed with no organization.
    const userId = override?.userId ?? state.user?.id;
    if (userId) {
      // ENT-4713: publish the now-admitted session synchronously before destination
      // routing can mount authenticated providers (shared with the no-org-context
      // path via publishAdmittedSession).
      if (override?.session?.user.id === userId) {
        publishAdmittedSession(override.session);
      }
      await fetchOrganizations(userId, {
        force: true,
        accessToken: override?.accessToken ?? state.session?.access_token ?? undefined,
        sessionUserId: override?.sessionUserId ?? state.session?.user?.id ?? undefined,
        markLoading: override?.markLoading === true,
      });
    }
  }, [state.session?.access_token, state.session?.user?.id, state.user, fetchOrganizations, publishAdmittedSession]);

  useEffect(() => {
    const bootstrapOrganizationContext = (userId: string, accessToken?: string | null) => {
      if (bootstrapUserIdRef.current === userId) {
        return;
      }

      const lastBootstrap = lastBootstrapAtRef.current;
      if (
        lastBootstrap &&
        lastBootstrap.userId === userId &&
        Date.now() - lastBootstrap.startedAt < BOOTSTRAP_COOLDOWN_MS
      ) {
        dispatch({ type: 'SET_LOADING', isLoading: false });
        return;
      }

      bootstrapUserIdRef.current = userId;
      lastBootstrapAtRef.current = {
        userId,
        startedAt: Date.now(),
      };
      const bootstrapFetch = fetchOrganizations(userId, {
        accessToken,
        sessionUserId: userId,
      });
      // ENT-4200 (Fix 5b, Codex R5 P1): `fetchOrganizations` claims its generation
      // synchronously, so this is the bootstrap's own token (or, on a cooldown-skip
      // / in-flight-coalesce early return, the generation currently in flight).
      // Every release below is generation-guarded, so a pre-claim bootstrap
      // settling under a newer `markLoading` refetch can no longer drop `isLoading`
      // — the state the newer fetch owns — and paint an empty shell. The settle
      // release here covers the early-return paths the fetch's own guarded
      // `finally` never reaches; elsewhere it is a reducer no-op.
      const bootstrapGeneration = fetchGenerationRef.current;
      void armLoadingReleaseTimeout(bootstrapFetch, bootstrapGeneration)
        .finally(() => {
          if (bootstrapGeneration === fetchGenerationRef.current) {
            dispatch({ type: 'SET_LOADING', isLoading: false });
          }
          if (bootstrapUserIdRef.current === userId) {
            bootstrapUserIdRef.current = null;
          }
        });
    };
    let bootstrapTimer: ReturnType<typeof setTimeout> | null = null;

    const refreshAuthSessionForE2e = async (): Promise<E2eAuthRefreshResult> => {
      const tokenRefreshEvent = createTokenRefreshEventWaiter();
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        tokenRefreshEvent.cleanup();
        return {
          ok: false,
          userId: data.session?.user?.id ?? null,
          error: error.message,
        };
      }

      try {
        const refreshed = await tokenRefreshEvent.promise;
        return {
          ok: true,
          ...refreshed,
        };
      } catch (refreshEventError) {
        return {
          ok: false,
          userId: data.session?.user?.id ?? null,
          error: refreshEventError instanceof Error
            ? refreshEventError.message
            : String(refreshEventError),
        };
      }
    };

    const exposeE2eAuthRefreshHook = shouldExposeE2eAuthRefreshHook();
    if (exposeE2eAuthRefreshHook) {
      window.__supplyGuardE2eRefreshAuthSession = refreshAuthSessionForE2e;
    }

    const scheduleBootstrapOrganizationContext = (userId: string, accessToken?: string) => {
      if (bootstrapTimer) {
        clearTimeout(bootstrapTimer);
      }

      bootstrapTimer = setTimeout(() => {
        bootstrapTimer = null;
        bootstrapOrganizationContext(userId, accessToken);
      }, 0);
    };

    const applyAuthStateChange = (event: AuthChangeEvent, session: Session | null) => {
      if (session?.access_token) {
        heldSessionIdRef.current = sessionIdOfAccessToken(session.access_token);
        lastAuthenticatedSessionIdentityRef.current = heldSessionIdRef.current
          ? `${session.user.id}:${heldSessionIdRef.current}`
          : `${session.user.id}:unknown-session`;
      }
      // ENT-4713: a fresh initial SAML session cannot yet satisfy the durable
      // admission guard. Publishing it here would make root providers start
      // protected reads before Index claims membership and records admission.
      // The successful claim explicitly re-enters through refetch({ session }).
      if (shouldDeferInitialSsoAdmission(session)) {
        syncMarketingAuthHintCookie(true);
        resetSessionExpiryGuard();
        dispatch({ type: 'SET_LOADING', isLoading: false });
        return;
      }

      // ENT-4771: a signed-out/rejected callback (claim rejection, ENT-3783
      // cutover sign-out) has nothing left to admit — release held reads.
      if (!session) {
        settleSsoCallbackAdmission();
        // ENT-5492: latch "session ended" before SET_AUTH routes to /auth. A
        // sign-out another tab owned involuntarily is adopted (Codex round 2);
        // a dead session the auth library signed out on its own ends here,
        // once (AC5/AC6); a voluntary sign-out marked itself and stays quiet.
        if (event === 'SIGNED_OUT') {
          endSessionSignedOutByAuthLibrary(heldSessionIdRef.current);
          heldSessionIdRef.current = null;
        }
      }

      // Always update auth state
      dispatch({
        type: 'SET_AUTH',
        user: session?.user ?? null,
        session: session ?? null,
        // ENT-3079: SIGNED_IN is also re-emitted for the SAME user when a tab
        // regains visibility (supabase-js session recovery). Preserving the
        // reference on those benign re-emits keeps downstream effect deps
        // stable so consumers (e.g. VendorContext) don't re-bootstrap with a
        // loading flash on tab return. The reducer's identity guard
        // (shouldPreserveAuthUserReference) still swaps the reference whenever
        // the user id or user data actually changed.
        preserveUserReference: event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN',
      });
      syncMarketingAuthHintCookie(Boolean(session));

      // A valid session is (re-)established — re-arm the terminal-auth once-guard
      // so a future expiry can trigger recovery again (ENT-2371 Codex P2).
      if (session?.user) {
        resetSessionExpiryGuard();
      }

      // Skip redundant refetch for token refreshes — session/user don't change
      if (SKIP_REFETCH_EVENTS.has(event)) {
        console.debug('[org] skipping refetch for event:', event);
        return;
      }

      if (session?.user) {
        const isNewAuthenticatedUser = lastAuthenticatedUserIdRef.current !== session.user.id;
        if (isNewAuthenticatedUser) {
          // The previous user's in-flight read and freshness window cannot
          // satisfy this identity's workspace bootstrap (ENT-6102).
          ++fetchGenerationRef.current;
          inFlightFetchRef.current = null;
          lastFetchRef.current = 0;
          dispatch({ type: 'CLEAR_ORG_DATA' });
          dispatch({ type: 'SET_LOADING', isLoading: true });
        }

        if (isUserEmailVerified(session.user)) {
          clearPendingSignupVerification();
        }

        if (
          lastAuthenticatedUserIdRef.current &&
          lastAuthenticatedUserIdRef.current !== session.user.id
        ) {
          clearOrganizationContextCache(localStorage, lastAuthenticatedUserIdRef.current);
          clearPersistedOrganizationSelection(localStorage);
        }

        lastAuthenticatedUserIdRef.current = session.user.id;
        const cached = readOrganizationContextCache(localStorage, session.user.id);
        syncPersistedOrganizationSelection(localStorage, cached, session.user.id);
        if (canHydrateFromCachedOrganizationSelection(localStorage, cached)) {
          if (cached.organization) {
            hydrateDepartmentsCache(cached.organization.id, cached.departments);
          }
          dispatch({
            type: 'SET_ORG_DATA',
            organization: cached.organization,
            membership: cached.membership,
            organizations: cached.organizations,
            mspCustomerOrgs: cached.mspCustomerOrgs,
            departments: cached.departments,
            isMspOrg: cached.isMspOrg,
            isMspContext: cached.isMspContext,
            mspHomeOrgId: cached.mspHomeOrgId,
          });
          dispatch({
            type: 'SET_BOOTSTRAP_DATA',
            organization: cached.organization,
            membership: cached.membership,
            isMspOrg: cached.isMspOrg,
            isMspContext: cached.isMspContext,
            mspHomeOrgId: cached.mspHomeOrgId,
            subscription: cached.subscription,
            supplierLimit: cached.supplierLimit ?? null,
          });
          dispatch({ type: 'SET_LOADING', isLoading: false });
        }
        scheduleBootstrapOrganizationContext(session.user.id, session.access_token);
      } else {
        if (bootstrapTimer) {
          clearTimeout(bootstrapTimer);
          bootstrapTimer = null;
        }
        lastBootstrapAtRef.current = null;
        bootstrapUserIdRef.current = null;
        if (lastAuthenticatedUserIdRef.current) {
          clearOrganizationContextCache(localStorage, lastAuthenticatedUserIdRef.current);
          lastAuthenticatedUserIdRef.current = null;
        }
        if (!shouldPreserveUnauthenticatedOrganizationSelection(localStorage)) {
          clearPersistedOrganizationSelection(localStorage);
        }
        dispatch({ type: 'CLEAR_ORG_DATA' });
        dispatch({ type: 'SET_LOADING', isLoading: false });
      }
    };

    let authEventSequence = 0;
    let authReconciliationTimer: ReturnType<typeof setTimeout> | null = null;
    let reconcilingSignedOut = false;
    let disposed = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const sequence = ++authEventSequence;
      if (authReconciliationTimer !== null) {
        clearTimeout(authReconciliationTimer);
        authReconciliationTimer = null;
      }
      // ENT-6102: an old tab's SIGNED_OUT broadcast can arrive after a new
      // login has persisted. A null event is then not evidence that the current
      // session ended. Withhold protected content and reconcile through Auth,
      // outside its callback lock, before triggering destructive teardown.
      if (event === 'SIGNED_OUT' && isClientSessionStored()) {
        reconcilingSignedOut = true;
        if (bootstrapTimer) {
          clearTimeout(bootstrapTimer);
          bootstrapTimer = null;
        }
        ++fetchGenerationRef.current;
        inFlightFetchRef.current = null;
        lastFetchRef.current = 0;
        lastBootstrapAtRef.current = null;
        bootstrapUserIdRef.current = null;
        dispatch({ type: 'SET_AUTH', user: null, session: null });
        dispatch({ type: 'SET_LOADING', isLoading: true });
        authReconciliationTimer = setTimeout(() => {
          authReconciliationTimer = null;
          void supabase.auth.getSession().then(({ data, error }) => {
            if (disposed || sequence !== authEventSequence) return;
            if (error) {
              reportSessionReconciliationFailure(error);
            }
            // A failed sign-out can remove storage while this read settles.
            // Never restore a session that teardown has since removed.
            const currentSession = !error && isClientSessionStored() ? data.session : null;
            reconcilingSignedOut = false;
            applyAuthStateChange(currentSession ? 'SIGNED_IN' : 'SIGNED_OUT', currentSession);
          }).catch((error: unknown) => {
            if (disposed || sequence !== authEventSequence) return;
            reportSessionReconciliationFailure(error);
            reconcilingSignedOut = false;
            applyAuthStateChange('SIGNED_OUT', null);
          });
        }, 0);
        return;
      }
      // A refresh can be the result of getSession's recovery. It supersedes
      // that read and must finish the withheld bootstrap, rather than taking
      // the ordinary refresh path that deliberately skips an org refetch.
      const completesReconciliation = reconcilingSignedOut && session !== null;
      reconcilingSignedOut = false;
      applyAuthStateChange(completesReconciliation && event === 'TOKEN_REFRESHED' ? 'SIGNED_IN' : event, session);
    });

    return () => {
      disposed = true;
      if (authReconciliationTimer !== null) clearTimeout(authReconciliationTimer);
      if (bootstrapTimer) {
        clearTimeout(bootstrapTimer);
      }
      if (
        exposeE2eAuthRefreshHook
        && window.__supplyGuardE2eRefreshAuthSession === refreshAuthSessionForE2e
      ) {
        delete window.__supplyGuardE2eRefreshAuthSession;
      }
      subscription.unsubscribe();
    };
  }, [fetchOrganizations]);

  const switchOrganizationContext = useCallback(async ({
    targetOrgId,
    targetKind,
    source,
    mspHomeOrgId: explicitMspHomeOrgId,
  }: OrganizationSwitchRequest) => {
    if (!state.user?.id) {
      return;
    }

    const ownOrg = state.organizations.find(o => o.id === targetOrgId);
    if ((ownOrg || source === 'supplier-plus') && targetKind !== 'managed-customer') {
      const bootstrapResponse = await invokeAppBootstrapReadModel({
        currentOrgId: targetOrgId,
        isMspContext: false,
        mspHomeOrgId: null,
      }, {
        expectedUserId: state.user.id,
        accessToken: state.session?.access_token ?? undefined,
        sessionUserId: state.session?.user?.id ?? undefined,
      });
      applyBootstrapSnapshot(state.user.id, bootstrapResponse);
      const response = await invokeOrganizationContextReadModel({
        currentOrgId: targetOrgId,
        isMspContext: false,
        mspHomeOrgId: null,
      }, {
        expectedUserId: state.user.id,
        accessToken: state.session?.access_token ?? undefined,
        sessionUserId: state.session?.user?.id ?? undefined,
      });
      applyOrganizationSnapshot(state.user.id, response);
      return;
    }

    const customerOrg = state.mspCustomerOrgs.find(o => o.id === targetOrgId);
    if (customerOrg || targetKind === 'managed-customer') {
      const homeOrgId = explicitMspHomeOrgId ?? (state.organization && !state.isMspContext ? state.organization.id : state.mspHomeOrgId);
      const bootstrapResponse = await invokeAppBootstrapReadModel({
        currentOrgId: targetOrgId,
        isMspContext: true,
        mspHomeOrgId: homeOrgId,
      }, {
        expectedUserId: state.user.id,
        accessToken: state.session?.access_token ?? undefined,
        sessionUserId: state.session?.user?.id ?? undefined,
      });
      const matchesExplicitDestination = (snapshot: AppBootstrapReadModelResponse | Awaited<ReturnType<typeof invokeOrganizationContextReadModel>>) =>
        snapshot.organization?.id === targetOrgId && snapshot.isMspContext && snapshot.mspHomeOrgId === explicitMspHomeOrgId;
      if (explicitMspHomeOrgId && !matchesExplicitDestination(bootstrapResponse)) throw new Error('advisory_forbidden');
      if (!explicitMspHomeOrgId) applyBootstrapSnapshot(state.user.id, bootstrapResponse);
      const response = await invokeOrganizationContextReadModel({
        currentOrgId: targetOrgId,
        isMspContext: true,
        mspHomeOrgId: homeOrgId,
      }, {
        expectedUserId: state.user.id,
        accessToken: state.session?.access_token ?? undefined,
        sessionUserId: state.session?.user?.id ?? undefined,
      });
      if (explicitMspHomeOrgId) {
        if (!matchesExplicitDestination(response)) throw new Error('advisory_forbidden');
        applyBootstrapSnapshot(state.user.id, bootstrapResponse);
      }
      applyOrganizationSnapshot(state.user.id, response);
      return;
    }

    throw new Error('unknown_organization_context_target');
  }, [
    applyBootstrapSnapshot,
    applyOrganizationSnapshot,
    state.organizations,
    state.mspCustomerOrgs,
    state.organization,
    state.isMspContext,
    state.mspHomeOrgId,
    state.session?.access_token,
    state.session?.user?.id,
    state.user,
  ]);

  const switchOrganization = useCallback(async (orgId: string) => {
    await switchOrganizationContext({
      targetOrgId: orgId,
      source: 'legacy',
    });
  }, [switchOrganizationContext]);

  const returnToMspOrg = useCallback(async () => {
    if (!state.user?.id) {
      return;
    }

    // Prefer the MSP home org. Otherwise fall back to the first org that
    // is NOT the current one (avoids returning to the break-glass org).
    const directMspHomeOrgId = state.mspHomeOrgId && state.organizations.some(o => o.id === state.mspHomeOrgId)
      ? state.mspHomeOrgId
      : null;
    const targetOrgId = directMspHomeOrgId
      ?? state.organizations.find(o => o.id !== state.organization?.id)?.id
      ?? state.organizations[0]?.id;
    if (!targetOrgId) {
      return;
    }

    await switchOrganizationContext({
      targetOrgId,
      source: 'customer-profile-exit',
    });
  }, [
    state.mspHomeOrgId,
    state.organization,
    state.organizations,
    state.user,
    switchOrganizationContext,
  ]);

  // Compute mspHomeOrg from mspHomeOrgId
  const mspHomeOrg = useMemo(
    () => state.mspHomeOrgId ? state.organizations.find(o => o.id === state.mspHomeOrgId) || null : null,
    [state.mspHomeOrgId, state.organizations]
  );

  // Memoize context value to prevent unnecessary re-renders in consumers
  const contextValue = useMemo<OrganizationContextType>(() => ({
    user: state.user,
    session: state.session,
    organization: state.organization,
    membership: state.membership,
    organizations: state.organizations,
    mspCustomerOrgs: state.mspCustomerOrgs,
    departments: state.departments,
    departmentsHydrated: state.departmentsHydrated,
    isMspOrg: state.isMspOrg,
    isLoading: state.isLoading,
    bootstrapError: state.bootstrapError,
    isMspContext: state.isMspContext,
    mspHomeOrg,
    mspHomeOrgId: state.mspHomeOrgId,
    bootstrapSubscription: state.bootstrapSubscription,
    bootstrapSupplierLimit: state.bootstrapSupplierLimit,
    switchOrganizationContext,
    switchOrganization,
    returnToMspOrg,
    refetch,
    publishAdmittedSession,
  }), [
    state.user, state.session, state.organization, state.membership,
    state.organizations, state.mspCustomerOrgs, state.departments, state.departmentsHydrated, state.isMspOrg, state.isLoading, state.bootstrapError,
    state.isMspContext, state.bootstrapSubscription, state.bootstrapSupplierLimit, mspHomeOrg, state.mspHomeOrgId, switchOrganizationContext, switchOrganization, returnToMspOrg, refetch, publishAdmittedSession,
  ]);

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
